import { parseISO } from "date-fns";
import React from "react";

import { Skeleton, Table, Text, formatDateTime } from "@performyard/py-components";
import type { PYColumnDef, SortChangedEvent } from "@performyard/py-components";
import type { WorkflowListItem } from "@performyard/service-contracts";

type SortDirection = "asc" | "desc";

const HEADER_HEIGHT = 44;
const ROW_HEIGHT = 48;
const EMPTY_STATE_HEIGHT = 356;
const SKELETON_ROW_COUNT = 5;

type AllWorkflowsTableProps = {
	workflows: WorkflowListItem[];
	isLoading: boolean;
	onSortChange: (sort: { name: string; direction: SortDirection }) => void;
	activeSort: string;
	activeSortDirection: SortDirection;
	emptyStateText: string;
};

const isLoadingRow = (data: Partial<WorkflowListItem>): boolean => !data._id;

const getProgressPercent = (workflow: WorkflowListItem): number => {
	if (workflow.totalTodoCount === 0) {
		return 100;
	}
	return Math.round((workflow.completedTodoCount / workflow.totalTodoCount) * 100);
};

// Workflow start dates are stored at midnight UTC, so format the date portion
// directly instead of converting to the viewer's timezone (which can shift the day).
const formatStartDate = (value: unknown): string => {
	if (!value) {
		return "";
	}
	return formatDateTime(parseISO(String(value).slice(0, 10)), { hasTime: false, dateSize: "medium" }) ?? "";
};

const AllWorkflowsTable = ({
	workflows,
	isLoading,
	onSortChange,
	activeSort,
	activeSortDirection,
	emptyStateText,
}: AllWorkflowsTableProps) => {
	const onSortChanged = (event: SortChangedEvent<WorkflowListItem>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			onSortChange({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as SortDirection,
			});
		}
	};

	const columns: PYColumnDef<WorkflowListItem>[] = [
		{
			colId: "name",
			headerName: "Workflow",
			flex: 1,
			minWidth: 200,
			sortable: true,
			cellRenderer: (params: { data: WorkflowListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				return (
					<a
						href={`/view-workflow/${row._id}?source=company-workflows`}
						className="text-primary-5 no-underline hover:text-primary-7 hover:underline"
					>
						<Text size="paragraph-3" weight="regular">
							<span className="py-line-clamp-1">{row.name}</span>
						</Text>
					</a>
				);
			},
		},
		{
			colId: "subject",
			headerName: "Assignee",
			type: "employee",
			width: 220,
			valueGetter: (params: { data?: WorkflowListItem }) => params.data?.subject ?? null,
		},
		{
			colId: "start_date",
			headerName: "Start Date",
			type: "date",
			width: 140,
			sortable: true,
			valueGetter: (params: { data?: WorkflowListItem }) => params.data?.startDate ?? null,
			valueFormatter: (params: { value: unknown }) => formatStartDate(params.value),
		},
		{
			colId: "progress",
			headerName: "Progress",
			type: "progress",
			width: 180,
			valueGetter: (params: { data?: WorkflowListItem }) => {
				const workflow = params.data;
				if (!workflow || isLoadingRow(workflow)) return null;
				return getProgressPercent(workflow);
			},
			cellRendererParams: {
				ariaLabel: "Workflow Progress",
			},
		},
		{
			colId: "overdue",
			headerName: "Overdue",
			width: 120,
			headerClass: "[&_.ag-header-cell-label]:justify-center",
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
			valueGetter: (params: { data?: WorkflowListItem }) => params.data?.overdueTodoCount ?? 0,
			cellRenderer: (params: { data: WorkflowListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-6" />;
				return row.overdueTodoCount;
			},
		},
	];

	const rowsLength = isLoading ? SKELETON_ROW_COUNT : workflows.length;
	const tableWrapperHeight =
		rowsLength === 0 ? `${EMPTY_STATE_HEIGHT}px` : `${HEADER_HEIGHT + rowsLength * ROW_HEIGHT}px`;

	return (
		<div
			data-testid="workflows-table"
			style={{
				height: tableWrapperHeight,
				transition: "height 150ms ease-out",
			}}
		>
			<Table<WorkflowListItem>
				columnDefs={columns}
				rowData={workflows}
				loading={isLoading}
				getRowId={(params) => params.data._id}
				rowHeight={ROW_HEIGHT}
				defaultColDef={{
					cellStyle: { display: "flex", alignItems: "center" },
					resizable: false,
					sortable: false,
					suppressMovable: true,
					sortingOrder: ["asc", "desc"],
				}}
				emptyStateText={emptyStateText}
				onSortChanged={onSortChanged}
				initialState={{
					sort: {
						sortModel: activeSort ? [{ colId: activeSort, sort: activeSortDirection }] : [],
					},
				}}
			/>
		</div>
	);
};

export default AllWorkflowsTable;
